import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { resolveAssetUrl } from "../../company/helpers/ComapnyHelpers";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface VehicleImageGalleryProps {
  images: string[];
  autoScroll?: boolean;
  idAnuncio: number;
}

export function VehicleImageGallery({
  images,
  autoScroll = false,
  idAnuncio,
}: VehicleImageGalleryProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [currentImage, setCurrentImage] = useState(0);
  const [index, setIndex] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const ITEM_WIDTH = isMobile ? 80 : 110;
  const GAP = isMobile ? 12 : 16;
  const STEP = ITEM_WIDTH + GAP;
  const viewportRef = useRef<HTMLDivElement>(null);

  const safeImages = useMemo(
    () => (images ?? []).filter((img): img is string => Boolean(img)),
    [images]
  );

  // Preload de imagens otimizado
  useEffect(() => {
    if (safeImages.length === 0) return;
    
    safeImages.forEach((src) => {
      const imgUrl = resolveAssetUrl(src, idAnuncio);
      const img = new Image();
      img.src = imgUrl;
    });
  }, [safeImages, idAnuncio]);

  // Atualiza a largura do viewport
  useEffect(() => {
    if (!viewportRef.current) return;
    
    const updateWidth = () => {
      if (viewportRef.current) {
        setViewportWidth(viewportRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Calcula quantas imagens são visíveis
  const visibleItems = useMemo(() => {
    if (viewportWidth === 0) return 1;
    return Math.floor(viewportWidth / STEP);
  }, [viewportWidth, STEP]);

  // Calcula o índice máximo
  const maxIndex = useMemo(() => {
    return Math.max(safeImages.length - visibleItems, 0);
  }, [safeImages.length, visibleItems]);

  // Verifica se deve mostrar os botões
  const showPrevButton = !isMobile && index > 0;
  const showNextButton = !isMobile && index < maxIndex;

  const prev = useCallback(() => {
    setIndex((i) => Math.max(i - 1, 0));
  }, []);

  const next = useCallback(() => {
    setIndex((i) => Math.min(i + 1, maxIndex));
  }, [maxIndex]);

  const getImageUrl = useCallback((img?: string) => {
    return resolveAssetUrl(img ?? "", idAnuncio);
  }, [idAnuncio]);

  return (
    <>
      {/* IMAGEM PRINCIPAL */}
      <Box
        width="100%"
        height={isMobile ? 260 : 520}
        borderRadius={2}
        overflow="hidden"
        bgcolor="#f1f5f9"
        sx={{
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        {safeImages.length === 0 ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
            height="100%"
            color="#999"
            fontSize={14}
          >
            Sem imagens disponíveis
          </Box>
        ) : (
          <Box
            component="img"
            src={getImageUrl(safeImages[currentImage])}
            decoding="async"
            loading="eager"
            alt={`Imagem do veículo ${currentImage + 1}`}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        )}
      </Box>

      {/* ESTEIRA */}
      <Box position="relative" width="100%" mt={2} ref={viewportRef}>
        {/* Botão Anterior - Só aparece quando há imagens para voltar */}
        {showPrevButton && (
          <IconButton
            onClick={prev}
            sx={{
              position: "absolute",
              left: 8,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              bgcolor: "rgba(0,0,0,0.5)",
              color: "white",
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: "rgba(0,0,0,0.75)",
                transform: "translateY(-50%) scale(1.1)",
              },
              backdropFilter: "blur(4px)",
            }}
          >
            <ChevronLeft />
          </IconButton>
        )}

        {/* Container da Esteira */}
        <Box
          overflow={isMobile ? "auto" : "hidden"}
          position="relative"
          width="100%"
          maxHeight={ITEM_WIDTH + 10}
          sx={{
            scrollSnapType: isMobile ? "x mandatory" : "none",
            "&::-webkit-scrollbar": {
              height: 6,
            },
            "&::-webkit-scrollbar-track": {
              bgcolor: "#f1f5f9",
              borderRadius: 3,
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#cbd5e1",
              borderRadius: 3,
              "&:hover": {
                bgcolor: "#94a3b8",
              },
            },
          }}
        >
          {safeImages.length === 0 ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height={ITEM_WIDTH}
              borderRadius={2}
              bgcolor="#f5f5f5"
              color="#999"
              fontSize={14}
            >
              Sem imagens disponíveis
            </Box>
          ) : (
            <Box
              display="flex"
              gap={GAP / 8}
              sx={{
                transform: isMobile ? "none" : `translateX(-${index * STEP}px)`,
                transition: isMobile ? "none" : "transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                willChange: "transform",
                px: isMobile ? 1 : 0,
              }}
            >
              {safeImages.map((img, i) => (
                <Box
                  key={i}
                  onClick={() => setCurrentImage(i)}
                  sx={{
                    width: ITEM_WIDTH,
                    height: ITEM_WIDTH,
                    flexShrink: 0,
                    borderRadius: 2,
                    scrollSnapAlign: isMobile ? "center" : "none",
                    cursor: "pointer",
                    position: "relative",
                    overflow: "hidden",
                    border: currentImage === i ? "3px solid" : "2px solid transparent",
                    borderColor: currentImage === i ? theme.palette.primary.main : "transparent",
                    opacity: currentImage === i ? 1 : 0.6,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      opacity: 1,
                      transform: "scale(1.05)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  <img
                    src={getImageUrl(img)}
                    alt={`Miniatura ${i + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Botão Próximo - Só aparece quando há mais imagens para avançar */}
        {showNextButton && (
          <IconButton
            onClick={next}
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              bgcolor: "rgba(0,0,0,0.5)",
              color: "white",
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: "rgba(0,0,0,0.75)",
                transform: "translateY(-50%) scale(1.1)",
              },
              backdropFilter: "blur(4px)",
            }}
          >
            <ChevronRight />
          </IconButton>
        )}
      </Box>
    </>
  );
}
